import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, Unlock, Save, History, Loader2, AlertTriangle, AlertCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import SerpPreview from "./SerpPreview";

const SITE = "https://acponline.co.nz";
const field = (v) => v === undefined || v === null ? "" : v;
const GENERIC = /^ACP Online\s*[—-]\s*New Zealand trade supplier/i;

function clientWarnings(f) {
  const w = [];
  const t = (f.seo_title || "").trim(), d = (f.meta_description || "").trim(), c = (f.canonical || "").trim();
  if (!t) w.push({ field: "seo_title", level: "error", message: "Missing SEO title." });
  else if (t.length > 60) w.push({ field: "seo_title", level: "warning", message: `Title is ${t.length} chars — keep ≤ 60 so it isn't truncated.` });
  if (!d) w.push({ field: "meta_description", level: "error", message: "Missing meta description." });
  else {
    if (d.length > 160) w.push({ field: "meta_description", level: "warning", message: `Description is ${d.length} chars — keep ≤ 160.` });
    if (GENERIC.test(d)) w.push({ field: "meta_description", level: "error", message: "This is the generic site description — use a page-specific one." });
  }
  if (c && !(c.startsWith("http") || c.startsWith("/"))) w.push({ field: "canonical", level: "error", message: "Canonical must be an absolute URL or start with /." });
  if ((f.custom_jsonld || "").trim()) { try { JSON.parse(f.custom_jsonld); } catch { w.push({ field: "custom_jsonld", level: "error", message: "Custom JSON-LD is not valid JSON." }); } }
  return w;
}

export default function SeoEditor({ etype, ekey, onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);

  const load = () => {
    setData(null);
    api.get(`/admin/seo/entity/${etype}/${encodeURIComponent(ekey)}`).then((r) => {
      setData(r.data);
      const e = r.data.effective;
      setForm({ ...e, keywords: Array.isArray(e.keywords) ? e.keywords.join(", ") : (e.keywords || "") });
      setWarnings(r.data.warnings || []);
    }).catch(() => { setData(false); });
  };
  useEffect(load, [etype, ekey]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/seo/entity/${etype}/${encodeURIComponent(ekey)}`, { seo: form });
      const e = r.data.effective;
      setForm({ ...e, keywords: Array.isArray(e.keywords) ? e.keywords.join(", ") : (e.keywords || "") });
      setData((d) => ({ ...d, meta_source: form.meta_description ? "manual" : d?.meta_source }));
      setWarnings(r.data.warnings || []);
      toast.success("SEO saved");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const openHistory = () => {
    setHistory([]);
    api.get(`/admin/seo/history/${etype}/${encodeURIComponent(ekey)}`).then((r) => setHistory(r.data.items));
  };
  const restore = async (id) => {
    if (!window.confirm("Restore the previous values from this change?")) return;
    try { await api.post(`/admin/seo/history/${id}/restore`); toast.success("Restored"); setHistory(null); load(); }
    catch { toast.error("Restore failed"); }
  };

  if (data === false) return <p className="text-sm text-destructive">Could not load this page's SEO.</p>;
  if (!data || !form) return <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>;

  const canonicalFull = form.canonical?.startsWith("http") ? form.canonical : `${SITE}${form.canonical || ""}`;
  const shown = [...clientWarnings(form), ...warnings.filter((w) => /Duplicate/.test(w.message))];
  const warn = (f) => shown.find((w) => w.field === f);
  const errs = shown.filter((w) => w.level === "error");
  const warns = shown.filter((w) => w.level === "warning");

  return (
    <div className="space-y-5" data-testid="seo-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold text-steel">{data.label}</h3>
          <p className="text-xs text-muted-foreground">{data.path} · source: {data.meta_source || "—"}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => set("seo_locked", !form.seo_locked)} data-testid="seo-lock-toggle"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${form.seo_locked ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
            {form.seo_locked ? <Lock size={13} /> : <Unlock size={13} />} {form.seo_locked ? "Manual lock ON" : "Manual lock off"}
          </button>
          <Button size="sm" variant="outline" onClick={openHistory} data-testid="seo-history-btn"><History size={14} className="mr-1.5" /> History</Button>
          <Button size="sm" className="bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={save} disabled={saving} data-testid="seo-save-btn">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Save
          </Button>
        </div>
      </div>

      {(errs.length > 0 || warns.length > 0) && (
        <div className="space-y-1" data-testid="seo-warnings">
          {errs.map((w, i) => <div key={i} className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded px-2 py-1.5"><AlertCircle size={13} /> {w.message}</div>)}
          {warns.map((w, i) => <div key={i} className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1.5"><AlertTriangle size={13} /> {w.message}</div>)}
        </div>
      )}

      {form.seo_locked && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">This page is locked — automated SEO and supplier imports will not overwrite these fields.</div>}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Fld label={`SEO Title (${(form.seo_title || "").length}/60)`} warn={warn("seo_title")}>
            <Input value={field(form.seo_title)} onChange={(e) => set("seo_title", e.target.value)} data-testid="fld-seo_title" />
          </Fld>
          <Fld label={`Meta Description (${(form.meta_description || "").length}/160)`} warn={warn("meta_description")}>
            <Textarea rows={3} value={field(form.meta_description)} onChange={(e) => set("meta_description", e.target.value)} data-testid="fld-meta_description" />
          </Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="URL Slug"><Input value={field(form.slug)} onChange={(e) => set("slug", e.target.value)} data-testid="fld-slug" /></Fld>
            <Fld label="Canonical URL" warn={warn("canonical")}><Input value={field(form.canonical)} onChange={(e) => set("canonical", e.target.value)} data-testid="fld-canonical" /></Fld>
          </div>
          <Fld label="H1 Heading"><Input value={field(form.h1)} onChange={(e) => set("h1", e.target.value)} data-testid="fld-h1" /></Fld>
          <Fld label="Keywords / search themes (comma separated)"><Input value={field(form.keywords)} onChange={(e) => set("keywords", e.target.value)} data-testid="fld-keywords" /></Fld>
          <Fld label="Image ALT text"><Input value={field(form.image_alt)} onChange={(e) => set("image_alt", e.target.value)} data-testid="fld-image_alt" /></Fld>
        </div>

        <div className="space-y-4">
          <SerpPreview title={form.seo_title} url={canonicalFull} description={form.meta_description} />
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Social (OG) Title"><Input value={field(form.og_title)} onChange={(e) => set("og_title", e.target.value)} data-testid="fld-og_title" /></Fld>
            <Fld label="Open Graph Image URL"><Input value={field(form.og_image)} onChange={(e) => set("og_image", e.target.value)} data-testid="fld-og_image" /></Fld>
          </div>
          <Fld label="Social (OG) Description"><Textarea rows={2} value={field(form.og_description)} onChange={(e) => set("og_description", e.target.value)} data-testid="fld-og_description" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Indexing"><Sel value={form.seo_index} onChange={(v) => set("seo_index", v)} opts={["auto", "index", "noindex"]} testid="fld-seo_index" /></Fld>
            <Fld label="Follow links"><Sel value={form.seo_follow} onChange={(v) => set("seo_follow", v)} opts={["auto", "follow", "nofollow"]} testid="fld-seo_follow" /></Fld>
          </div>
          <Fld label="Structured data type"><Sel value={form.schema_type || ""} onChange={(v) => set("schema_type", v)} opts={["", "Product", "WebSite", "WebPage", "CollectionPage", "Organization", "BreadcrumbList", "FAQPage"]} testid="fld-schema_type" /></Fld>
          <Fld label="Custom JSON-LD (optional)" warn={warn("custom_jsonld")}>
            <Textarea rows={3} className="font-mono text-xs" value={field(form.custom_jsonld)} onChange={(e) => set("custom_jsonld", e.target.value)} data-testid="fld-custom_jsonld" />
          </Fld>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.breadcrumb} onCheckedChange={(v) => set("breadcrumb", v)} data-testid="fld-breadcrumb" /> Show breadcrumb</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.seo_automation} onCheckedChange={(v) => set("seo_automation", v)} data-testid="fld-seo_automation" /> SEO automation</label>
          </div>
        </div>
      </div>

      <Dialog open={history !== null} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="seo-history-dialog">
          <DialogHeader><DialogTitle>Change history</DialogTitle><DialogDescription>Every manual, AI or import change. Restore puts the previous values back.</DialogDescription></DialogHeader>
          {history === null ? null : history.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No changes recorded yet.</p>
            : <div className="divide-y">
                {history.map((h) => (
                  <div key={h.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="text-xs">
                      <div className="font-semibold text-steel">{new Date(h.at).toLocaleString()} · <span className="uppercase">{h.source}</span> · {h.editor}</div>
                      <div className="text-muted-foreground mt-1">{h.changes.map((c) => c.field).join(", ")}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => restore(h.id)} data-testid={`restore-${h.id}`}><RotateCcw size={12} className="mr-1" /> Restore</Button>
                  </div>
                ))}
              </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, warn, children }) {
  return (
    <div>
      <Label className={`text-xs font-semibold ${warn ? (warn.level === "error" ? "text-red-600" : "text-amber-600") : "text-steel"}`}>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Sel({ value, onChange, opts, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border px-2 text-sm bg-white" data-testid={testid}>
      {opts.map((o) => <option key={o} value={o}>{o === "" ? "— none —" : o}</option>)}
    </select>
  );
}
